import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { IconSun, IconMoon } from './Icons';
import Sidebar from './Sidebar';
import './Navbar.css';

const navLinks = [
  { to: '/',              label: 'Home' },
  { to: '/about',         label: 'About' },
  { to: '/research',      label: 'Research & Publications' },
  { to: '/awards',        label: 'Awards & Grants' },
  { to: '/organizations', label: 'Organizations' },
  { to: '/partnerships',  label: 'Partnerships' },
  { to: '/eco-village',   label: 'Eco-Village' },
  { to: '/speaking',      label: 'Speaking' },
  { to: '/gallery',       label: 'Gallery' },
  { to: '/contact',       label: 'Contact' },
];

/**
 * Premium Navbar Component
 * Features a sliding active highlight and smooth page transitions.
 */
export default function Navbar() {
  const { theme, toggleTheme } = useTheme();
  const [scrolled,  setScrolled]  = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);
  const location = useLocation();

  // Scroll visibility logic
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <header 
        className={`navbar ${scrolled ? 'navbar--scrolled' : ''} ${menuOpen ? 'navbar--hidden' : ''}`} 
        role="banner"
      >
        <div className="navbar__inner">
          {/* Brand */}
          <Link to="/" className="navbar__brand" aria-label="Dicky Sofjan, Ph.D. — Home">
            <motion.div 
              whileHover={{ scale: 1.05 }}
              transition={{ type: 'spring', stiffness: 400, damping: 10 }}
            >
              <img src="/dr sofjan/2.jpeg" alt="Dicky Sofjan logo" className="navbar__brand-monogram" />
            </motion.div>
            <span className="navbar__brand-text">
              <strong>Dicky Sofjan</strong>
            </span>
          </Link>

          {/* Desktop nav with Sliding Highlight */}
          <nav className="navbar__links" aria-label="Primary navigation">
            {navLinks.slice(1).map(link => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) => `navbar__link ${isActive ? 'navbar__link--active' : ''}`}
              >
                {({ isActive }) => (
                  <>
                    <span className="navbar__link-content">{link.label}</span>
                    {isActive && (
                      <motion.div
                        layoutId="active-highlight"
                        className="navbar__active-pill"
                        initial={false}
                        transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Actions */}
          <div className="navbar__actions">
            <motion.button
              whileHover={{ scale: 1.1, rotate: 15 }}
              whileTap={{ scale: 0.9 }}
              className="navbar__theme-btn"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={theme}
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  transition={{ duration: 0.2 }}
                >
                  {theme === 'light' ? <IconMoon size={18} /> : <IconSun size={18} />}
                </motion.div>
              </AnimatePresence>
            </motion.button>

            <motion.div 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
            >
              <Link to="/contact" className="btn btn-gold btn-sm navbar__cta">
                Get in Touch
              </Link>
            </motion.div>

            {/* Hamburger */}
            <button
              className={`navbar__hamburger ${menuOpen ? 'navbar__hamburger--open' : ''}`}
              onClick={() => setMenuOpen(true)}
              aria-label="Open navigation menu"
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
      </header>

      {/* New Creative Mobile Sidebar */}
      <Sidebar 
        isOpen={menuOpen} 
        onClose={() => setMenuOpen(false)} 
        navLinks={navLinks} 
        theme={theme}
        onToggleTheme={toggleTheme}
      />
    </>
  );
}
